import { useEffect, useRef, useState } from 'react';

export const VIEW_COUNT_KEY_PREFIX = 'newsletterViewCount';
export const LAST_HIDE_DATE_KEY_PREFIX = 'newsletterLastHideDate';
export const ONE_MONTH_IN_MILISECONDS = 30 * 24 * 60 * 60 * 1000;
export const ONE_WEEK_IN_MILISECONDS = 7 * 24 * 60 * 60 * 1000;
export const HIDE_PERIOD_IN_MILISECONDS = ONE_MONTH_IN_MILISECONDS;
export const VIEW_PERIOD_IN_MS = ONE_MONTH_IN_MILISECONDS;
export const MAX_VIEWS_BEFORE_HIDE = 10;

const isLocalStorageSupported = (): boolean => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
  } catch (_) {
    return false;
  }

  return true;
};

interface IntersectorHandlerProps {
  viewCountKey: string;
  hidePeriodStartDateKey: string;
  maxViewsBeforeHide: number;
  viewPeriodInMs: number;
}

export const handleView = ({
  viewCountKey,
  hidePeriodStartDateKey,
  maxViewsBeforeHide,
  viewPeriodInMs,
}: IntersectorHandlerProps) => {
  const now = new Date().getTime();

  const views = [
    ...(
      JSON.parse(localStorage.getItem(viewCountKey) ?? '[]') as number[]
    ).filter((viewed) => now - viewed <= viewPeriodInMs),
    now,
  ];
  const hasReachedMaxViewsInPeriod = views.length >= maxViewsBeforeHide;

  if (hasReachedMaxViewsInPeriod) {
    /** Do not set shouldHide to true here,
     * as it will cause the widget to disappear instantly on view,
     * a potential jarring user experience.
     * Rely on the useEffect above to set shouldHide to true on next load. */
    localStorage.setItem(hidePeriodStartDateKey, now.toString());
    localStorage.removeItem(viewCountKey);
  } else {
    localStorage.setItem(viewCountKey, JSON.stringify(views));
  }
};

/**
 * Determines whether the newsletter component should be hidden for a period based on the number of recent views.
 * @param newsletterId - The ID of the newsletter.
 * @param canSubscribe - A boolean indicating whether the user is allowed to subscribe to the newsletter.
 * @param accountReady - A boolean indicating whether account is ready.
 * @returns An object containing a boolean indicating whether the component should be hidden and a ref to the newsletter component.
 */
const useShouldHideDueToViews = (
  newsletterId: string,
  canSubscribe: boolean,
  accountReady: boolean,
) => {
  const [shouldHide, setShouldHide] = useState(false);
  const newsletterRef = useRef<HTMLDivElement | null>(null);
  const node = newsletterRef.current;
  const viewCountKey = `${VIEW_COUNT_KEY_PREFIX}-${newsletterId}`;
  const hidePeriodStartDateKey = `${LAST_HIDE_DATE_KEY_PREFIX}-${newsletterId}`;

  /**
   * This useEffect checks on article load if hide period is active, and hides the newsletter if so.
   */
  useEffect(() => {
    const endHidePeriod = () => {
      localStorage.removeItem(hidePeriodStartDateKey);
      localStorage.removeItem(viewCountKey);
      setShouldHide(false);
    };

    if (accountReady && !canSubscribe) {
      /** End any ongoing hide period if user can no longer subscribe to the newsletter,
       * e.g. when cancelling Vink subscription. */
      endHidePeriod();

      return;
    }
    const now = new Date().getTime();

    const hidePeriodStart = Number(
      localStorage.getItem(hidePeriodStartDateKey),
    );

    const hasPassedMaxHideTime =
      hidePeriodStart && now - hidePeriodStart >= HIDE_PERIOD_IN_MILISECONDS;

    if (hasPassedMaxHideTime) {
      endHidePeriod();
    } else if (hidePeriodStart) {
      // Hide period is active, hide widget.
      setShouldHide(true);
    }
  }, [
    accountReady,
    canSubscribe,
    hidePeriodStartDateKey,
    newsletterId,
    viewCountKey,
  ]);

  // This useEffect handles view counting.
  useEffect(() => {
    const shouldInitializeObserver =
      isLocalStorageSupported() &&
      'IntersectionObserver' in window &&
      node &&
      accountReady &&
      canSubscribe;

    if (!shouldInitializeObserver) {
      return () => undefined;
    }

    const observer = new IntersectionObserver(
      ([{ isIntersecting, target }]) => {
        if (!isIntersecting) return;

        handleView({
          viewCountKey,
          hidePeriodStartDateKey,
          maxViewsBeforeHide: MAX_VIEWS_BEFORE_HIDE,
          viewPeriodInMs: VIEW_PERIOD_IN_MS,
        });

        // Unobserve so we only increment view count once per page load.
        observer.unobserve(target);
      },
      //TODO: adjust threshold?
      { threshold: 1 },
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if ('IntersectionObserver' in window && node) {
        observer.disconnect();
      }
    };
  }, [
    accountReady,
    canSubscribe,
    hidePeriodStartDateKey,
    newsletterId,
    node,
    viewCountKey,
  ]);

  return { shouldHide, newsletterRef };
};

export { useShouldHideDueToViews };
